import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  TrendingUp, CreditCard, Calculator, Shield, FileText,
  Plus, Trash2, RefreshCw, CheckCircle, AlertTriangle, Save, X
} from 'lucide-react';
import Portal from '../UI/Portal';
import { InfoBadge } from '../UI/InfoBadge';
import FormSelect from '../UI/FormSelect';
import DatePickerField from '../UI/DatePickerField';
import {
  useAdjustPayroll,
  useBackpayPreview,
  usePreviewAdjustment,
  buildPayrollAdjustmentLineItem,
  DEDUCTION_ADJUSTMENT_TYPES
} from '../../hooks';
import {
  formatDate,
  formatMonthYear,
  parseLocalDate,
  toLocalISODate,
} from '../../utils/dateUtils';
import toast from '../../utils/toast';

const INITIAL_FORM = {
  backpayItems: [],
  overtimeItems: [],
  allowanceItems: [],
  bonusItems: [],
  deductionItems: [],
  reimbursementItems: [],
  adjustmentNotes: '',
  bonusPayments: '',
  otherPayments: '',
  otherDeductions: ''
};

const mapStoredDeductionItem = (item) => {
  const desc = (item.description || '').trim();
  if (!desc) return { description: '', amount: String(item.amount || ''), deductionType: '' };
  const match = DEDUCTION_ADJUSTMENT_TYPES.find(
    t => t.value !== 'other' && t.label.toLowerCase() === desc.toLowerCase()
  );
  return {
    description: desc,
    amount: String(item.amount || ''),
    deductionType: match ? match.value : 'other',
  };
};

const mapStoredLineItem = (item) => ({
  description: item.description || '',
  amount: String(item.amount || ''),
});

const EMPTY_BACKPAY_ITEM = {
  description: '',
  amount: '',
  targetDate: '',
  dailyRate: '',
  monthlyBasicSalary: '',
  momWorkingDays: '',
  workWeekType: '',
  rateBasis: '',
  rateSource: '',
  sourcePayrollId: null,
  suggestedAmount: '',
  correctedEntitlement: '',
  amountAlreadyPaid: '',
  hoursWorked: '',
  entitlementType: '',
  dayType: '',
  dayMultiplier: '',
  tier: '',
  otHours: '',
  otHourlyRate: '',
  calculationFingerprint: '',
  calculationVersion: '',
  calculationSummary: '',
  requiresManualReview: false,
  reviewReasons: [],
  wasLocked: false,
  isCurrentlyLocked: false,
  amendedAfterLock: false,
  attendanceRecordId: null,
  sourceLockIds: [],
  clockEventIds: [],
  lockAppliedAt: '',
  amendmentTimestamp: '',
  amendmentByStaffId: '',
  rateStatus: 'idle',
  rateError: '',
  isRateOverridden: false,
  overrideReason: '',
  lookupRequestId: null,
};

const applyBackpayPreview = (item, preview) => {
  const requiresManualReview = Boolean(preview.requires_manual_review);
  return {
    ...item,
    amount: requiresManualReview ? '' : String(preview.amount || ''),
    dailyRate: String(preview.daily_rate || ''),
    monthlyBasicSalary: String(preview.monthly_basic_salary || ''),
    momWorkingDays: String(preview.mom_working_days || ''),
    workWeekType: preview.work_week_type || '',
    rateBasis: preview.rate_basis || '',
    rateSource: preview.source || '',
    sourcePayrollId: preview.source_payroll_id || null,
    suggestedAmount: String(preview.suggested_amount || preview.amount || ''),
    correctedEntitlement: String(preview.corrected_entitlement || ''),
    amountAlreadyPaid: String(preview.amount_already_paid || ''),
    hoursWorked: String(preview.hours_worked || ''),
    entitlementType: preview.entitlement_type || '',
    dayType: preview.day_type || '',
    dayMultiplier: preview.day_multiplier || '',
    tier: preview.tier || '',
    otHours: preview.ot_hours || '',
    otHourlyRate: preview.ot_hourly_rate || '',
    calculationFingerprint: preview.calculation_fingerprint || '',
    calculationVersion: preview.calculation_version || '',
    calculationSummary: preview.calculation_summary || '',
    requiresManualReview,
    reviewReasons: preview.review_reasons || [],
    wasLocked: Boolean(preview.was_locked),
    isCurrentlyLocked: Boolean(preview.is_currently_locked),
    amendedAfterLock: Boolean(preview.amended_after_lock),
    attendanceRecordId: preview.attendance_record_id || null,
    sourceLockIds: preview.lock_ids || preview.source_lock_ids || [],
    clockEventIds: preview.clock_event_ids || [],
    lockAppliedAt: preview.lock_applied_at || '',
    amendmentTimestamp: preview.amendment_timestamp || '',
    amendmentByStaffId: preview.amendment_by_staff_id || '',
    rateStatus: requiresManualReview ? 'manual_review' : 'success',
    rateError: '',
    isRateOverridden: false,
    overrideReason: '',
  };
};

const mapStoredBackpayItem = (item) => {
  const amount = String(item.amount || '');
  const suggestedAmount = String(item.suggested_amount || item.daily_rate || '');
  const isOverridden = Boolean(
    item.is_rate_overridden
    || (suggestedAmount && amount && Number(suggestedAmount) !== Number(amount))
  );
  return {
    ...EMPTY_BACKPAY_ITEM,
    ...mapStoredLineItem(item),
    targetDate: item.target_date || '',
    dailyRate: String(item.daily_rate || ''),
    monthlyBasicSalary: String(item.monthly_basic_salary || ''),
    momWorkingDays: String(item.mom_working_days || ''),
    workWeekType: item.work_week_type || '',
    rateBasis: item.rate_basis || '',
    rateSource: item.rate_source || '',
    sourcePayrollId: item.source_payroll_id || null,
    suggestedAmount,
    correctedEntitlement: String(item.corrected_entitlement || ''),
    amountAlreadyPaid: String(item.amount_already_paid || ''),
    hoursWorked: String(item.hours_worked || ''),
    entitlementType: item.entitlement_type || '',
    dayType: item.day_type || '',
    dayMultiplier: item.day_multiplier || '',
    tier: item.tier || '',
    otHours: item.ot_hours || '',
    otHourlyRate: item.ot_hourly_rate || '',
    calculationFingerprint: item.calculation_fingerprint || '',
    calculationVersion: item.calculation_version || '',
    calculationSummary: item.calculation_summary || '',
    requiresManualReview: Boolean(item.requires_manual_review),
    wasLocked: Boolean(item.was_locked),
    isCurrentlyLocked: Boolean(item.is_currently_locked),
    amendedAfterLock: Boolean(item.amended_after_lock),
    attendanceRecordId: item.attendance_record_id || null,
    sourceLockIds: item.source_lock_ids || item.lock_ids || [],
    clockEventIds: item.clock_event_ids || [],
    lockAppliedAt: item.lock_applied_at || '',
    amendmentTimestamp: item.amendment_timestamp || '',
    amendmentByStaffId: item.amendment_by_staff_id || '',
    rateStatus: item.requires_manual_review
      ? 'manual_review'
      : (item.target_date && (suggestedAmount || amount) ? 'success' : 'idle'),
    rateError: '',
    isRateOverridden: isOverridden,
    overrideReason: item.override_reason || '',
    lookupRequestId: null,
  };
};

const PayrollAdjustmentModal = ({ isOpen, onClose, payrollData, formatCurrency, onSuccess }) => {
  const [adjustmentForm, setAdjustmentForm] = useState(INITIAL_FORM);
  const [adjustmentPreview, setAdjustmentPreview] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const previewTimeoutRef = useRef(null);
  const backpayLookupSequenceRef = useRef(0);

  const adjustPayrollMutation = useAdjustPayroll();
  const backpayPreviewMutation = useBackpayPreview();
  const { mutate: previewAdjustment } = usePreviewAdjustment();
  const employeePayrollId = payrollData?.id;

  const handleClose = useCallback(() => {
    setAdjustmentPreview(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose, isOpen]);

  useEffect(() => {
    if (isOpen) {
      // Primary source: dedicated adjustment_line_items field
      // Fallback: legacy location in other_deductions_breakdown.line_items
      const adjItems = payrollData?.adjustment_line_items || {};
      const legacyBreakdown = payrollData?.other_deductions_breakdown || {};
      const lineItems = (adjItems.backpay || adjItems.deductions || adjItems.overtime)
        ? adjItems
        : legacyBreakdown.line_items;
      const hasLineItems = lineItems && Object.values(lineItems).some(
        arr => Array.isArray(arr) && arr.length > 0
      );

      if (hasLineItems) {
        setAdjustmentForm({
          backpayItems: (lineItems.backpay || []).map(mapStoredBackpayItem),
          overtimeItems: (lineItems.overtime || []).map(mapStoredLineItem),
          allowanceItems: (lineItems.allowances || []).map(mapStoredLineItem),
          bonusItems: (lineItems.bonus || []).map(mapStoredLineItem),
          deductionItems: (lineItems.deductions || []).map(mapStoredDeductionItem),
          reimbursementItems: (lineItems.reimbursements || []).map(mapStoredLineItem),
          adjustmentNotes: '',
          bonusPayments: '',
          otherPayments: '',
          otherDeductions: '',
        });
      } else {
        // Check for calculated deductions (flat dict from calculation engine)
        // Format: {"Salary Overpayment Recovery": 33.33, "IDD Charges": 10.00}
        const calculatedDeductions = Object.entries(legacyBreakdown)
          .filter(([, val]) => typeof val === 'number' && val > 0)
          .map(([desc, amount]) => mapStoredDeductionItem({ description: desc, amount }));

        if (calculatedDeductions.length > 0) {
          setAdjustmentForm({
            ...INITIAL_FORM,
            deductionItems: calculatedDeductions,
          });
        } else {
          setAdjustmentForm(INITIAL_FORM);
        }
      }
      setAdjustmentPreview(null);
    }
  }, [isOpen, payrollData]);

  useEffect(() => {
    if (!isOpen || !employeePayrollId) return;

    if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);

    const isResolvingBackpayRate = adjustmentForm.backpayItems.some(
      item => item.rateStatus === 'loading'
    );
    if (isResolvingBackpayRate) {
      setIsPreviewLoading(true);
      return;
    }

    const hasAdjustments =
      adjustmentForm.backpayItems.length > 0 ||
      adjustmentForm.overtimeItems.length > 0 ||
      adjustmentForm.allowanceItems.length > 0 ||
      adjustmentForm.bonusItems.length > 0 ||
      adjustmentForm.deductionItems.length > 0 ||
      adjustmentForm.reimbursementItems.length > 0;

    if (!hasAdjustments) {
      setAdjustmentPreview(null);
      setIsPreviewLoading(false);
      return;
    }

    setIsPreviewLoading(true);

    previewTimeoutRef.current = setTimeout(() => {
      previewAdjustment({
        employeePayrollId,
        adjustmentData: {
          backpay_items: adjustmentForm.backpayItems
            .filter(i => i.amount)
            .map(i => buildPayrollAdjustmentLineItem(i, 'Backpay')),
          overtime_items: adjustmentForm.overtimeItems
            .filter(i => i.amount)
            .map(i => buildPayrollAdjustmentLineItem(i, 'Overtime')),
          allowance_items: adjustmentForm.allowanceItems
            .filter(i => i.amount)
            .map(i => buildPayrollAdjustmentLineItem(i, 'Allowance')),
          bonus_items: adjustmentForm.bonusItems
            .filter(i => i.amount)
            .map(i => buildPayrollAdjustmentLineItem(i, 'Bonus')),
          deduction_items: adjustmentForm.deductionItems
            .filter(i => i.amount)
            .map(i => buildPayrollAdjustmentLineItem(i, 'Deduction')),
          reimbursement_items: adjustmentForm.reimbursementItems
            .filter(i => i.amount)
            .map(i => buildPayrollAdjustmentLineItem(i, 'Reimbursement')),
        }
      }, {
        onSuccess: (data) => {
          setAdjustmentPreview(data);
          setIsPreviewLoading(false);
        },
        onError: () => {
          setIsPreviewLoading(false);
        }
      });
    }, 800);

    return () => {
      if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    };
  }, [
    isOpen,
    employeePayrollId,
    previewAdjustment,
    adjustmentForm.backpayItems,
    adjustmentForm.overtimeItems,
    adjustmentForm.allowanceItems,
    adjustmentForm.bonusItems,
    adjustmentForm.deductionItems,
    adjustmentForm.reimbursementItems,
  ]);

  if (!isOpen || !payrollData) return null;

  const handleApply = () => {
    const unresolvedReview = adjustmentForm.backpayItems.some(
      item => item.rateStatus === 'manual_review' && !parseFloat(item.amount)
    );
    if (unresolvedReview) {
      toast.error('A backpay date needs manual review. Enter an amount with notes, or remove the line.');
      return;
    }
    const hasOverride = adjustmentForm.backpayItems.some((item) => {
      if (!parseFloat(item.amount)) return false;
      if (item.isRateOverridden) return true;
      if (item.suggestedAmount === '') return false;
      return Number(item.amount) !== Number(item.suggestedAmount);
    });
    if (hasOverride && !adjustmentForm.adjustmentNotes.trim()) {
      toast.error('Add notes explaining why the suggested backpay amount was changed.');
      return;
    }
    adjustPayrollMutation.mutate({
      employeePayrollId: payrollData.id,
      adjustmentData: adjustmentForm
    }, {
      onSuccess: () => {
        handleClose();
        if (onSuccess) onSuccess();
      }
    });
  };

  const payrollPeriodStart = (
    payrollData.payroll_run?.period_start_date
    || payrollData.payroll_run_period_start_date
  );
  const periodStartDate = parseLocalDate(payrollPeriodStart);
  const periodLastDate = periodStartDate
    ? new Date(
      periodStartDate.getFullYear(),
      periodStartDate.getMonth(),
      periodStartDate.getDate() - 1
    )
    : undefined;
  const minBackpayDate = parseLocalDate(payrollData.employee_employment_start_date);
  const employmentEndDate = parseLocalDate(payrollData.employee_employment_end_date);
  const maxBackpayDate = periodLastDate && employmentEndDate
    ? new Date(Math.min(periodLastDate.getTime(), employmentEndDate.getTime()))
    : periodLastDate || employmentEndDate || undefined;

  const handleBackpayDateChange = async (index, selectedDate) => {
    const targetDate = selectedDate ? toLocalISODate(selectedDate) : '';
    const currentItem = adjustmentForm.backpayItems[index] || {};
    const currentDescription = currentItem.description || '';
    const descriptionWasGenerated = currentDescription.startsWith('Backpay for ');
    const lookupRequestId = targetDate
      ? ++backpayLookupSequenceRef.current
      : null;

    setAdjustmentForm(prev => {
      const updated = [...prev.backpayItems];
      updated[index] = {
        ...EMPTY_BACKPAY_ITEM,
        ...updated[index],
        targetDate,
        description: targetDate
          ? (
            !currentDescription || descriptionWasGenerated
              ? `Backpay for ${formatDate(targetDate)}`
              : currentDescription
          )
          : (descriptionWasGenerated ? '' : currentDescription),
        amount: '',
        suggestedAmount: '',
        calculationFingerprint: '',
        calculationSummary: '',
        requiresManualReview: false,
        reviewReasons: [],
        rateStatus: targetDate ? 'loading' : 'idle',
        rateError: '',
        isRateOverridden: false,
        lookupRequestId,
      };
      return { ...prev, backpayItems: updated };
    });

    if (!targetDate) return;

    try {
      const preview = await backpayPreviewMutation.mutateAsync({
        employeePayrollId: payrollData.id,
        targetDate,
      });
      setAdjustmentForm(prev => {
        const updated = [...prev.backpayItems];
        if (
          updated[index]?.targetDate !== targetDate
          || updated[index]?.lookupRequestId !== lookupRequestId
        ) {
          return prev;
        }
        updated[index] = applyBackpayPreview(updated[index], preview);
        return { ...prev, backpayItems: updated };
      });
    } catch (error) {
      setAdjustmentForm(prev => {
        const updated = [...prev.backpayItems];
        if (
          updated[index]?.targetDate !== targetDate
          || updated[index]?.lookupRequestId !== lookupRequestId
        ) {
          return prev;
        }
        updated[index] = {
          ...updated[index],
          rateStatus: 'error',
          rateError: (
            error.response?.data?.error
            || 'Attendance-aware backpay could not be calculated.'
          ),
        };
        return { ...prev, backpayItems: updated };
      });
    }
  };

  const renderBackpaySection = () => (
    <div className="mb-4">
      <div className="mb-2">
        <label className="form-label-admin !mb-0">Backpay</label>
        <p className="mt-1 text-xs text-gray-500">
          Select when pay was owed. We'll inspect attendance and calculate what remains unpaid.
        </p>
      </div>
      <div className="space-y-2">
        {adjustmentForm.backpayItems.map((item, index) => (
          <div key={index} className="rounded-xl border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1 overflow-visible p-0.5 -m-0.5">
                <label className="sr-only" htmlFor={`backpay-date-${index}`}>
                  Backpay date {index + 1}
                </label>
                <DatePickerField
                  id={`backpay-date-${index}`}
                  theme="admin"
                  selected={parseLocalDate(item.targetDate)}
                  onChange={(date) => handleBackpayDateChange(index, date)}
                  minDate={minBackpayDate || undefined}
                  maxDate={maxBackpayDate}
                  placeholderText="Date owed"
                  isClearable
                />
              </div>
              <div className="relative w-32 flex-shrink-0">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  onWheel={(event) => event.currentTarget.blur()}
                  value={item.amount}
                  onChange={(event) => {
                    const amount = event.target.value;
                    const suggested = item.suggestedAmount;
                    const updated = [...adjustmentForm.backpayItems];
                    updated[index] = {
                      ...updated[index],
                      amount,
                      isRateOverridden: Boolean(
                        suggested !== ''
                        && amount !== ''
                        && Number(amount) !== Number(suggested)
                      ),
                    };
                    setAdjustmentForm(prev => ({ ...prev, backpayItems: updated }));
                  }}
                  disabled={item.rateStatus === 'loading'}
                  aria-label="Backpay amount"
                  className="input-admin w-full text-sm pl-7 text-right tabular-nums disabled:bg-gray-100 disabled:text-gray-400"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const updated = adjustmentForm.backpayItems.filter((_, itemIndex) => itemIndex !== index);
                  setAdjustmentForm(prev => ({ ...prev, backpayItems: updated }));
                }}
                disabled={backpayPreviewMutation.isPending}
                className="icon-button-admin icon-button-sm flex-shrink-0 disabled:cursor-not-allowed disabled:opacity-40"
                title="Remove backpay"
                aria-label="Remove backpay"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="mt-2 min-h-[18px]" aria-live="polite">
              {item.rateStatus === 'loading' && (
                <p className="flex items-center gap-1.5 text-xs text-gray-500">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin text-red-600" />
                  Checking attendance for {formatDate(item.targetDate)}…
                </p>
              )}
              {item.rateStatus === 'success' && (
                <div className="space-y-1.5">
                  <p className="flex items-start gap-1.5 text-xs text-gray-600">
                    <CheckCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-green-600" />
                    <span>
                      <span className="font-medium text-gray-800">
                        {item.calculationSummary
                          || `${formatMonthYear(parseLocalDate(item.targetDate))} · ${formatCurrency(item.suggestedAmount || item.dailyRate)}`}
                      </span>
                    </span>
                  </p>
                  {item.amendedAfterLock && (
                    <div className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
                      <span>
                        Attendance was corrected after this day was locked.
                        The locked record stays frozen; this amount is next-payroll evidence
                        {item.amendmentTimestamp ? ` from ${formatDate(item.amendmentTimestamp)}` : ''}.
                      </span>
                    </div>
                  )}
                  {item.isRateOverridden && (
                    <p className="pl-5 text-xs text-amber-700">
                      Amount edited manually; verified suggestion remains {formatCurrency(item.suggestedAmount)}. Notes are required.
                    </p>
                  )}
                </div>
              )}
              {item.rateStatus === 'manual_review' && (
                <div className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
                  <span>
                    {(item.reviewReasons && item.reviewReasons[0])
                      || item.calculationSummary
                      || 'This date needs a manual review. Enter an amount and notes, or remove the line.'}
                  </span>
                </div>
              )}
              {item.rateStatus === 'error' && (
                <p className="flex items-start gap-1.5 text-xs text-red-700" role="alert">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span>{item.rateError} Enter the amount manually.</span>
                </p>
              )}
              {item.rateStatus === 'idle' && (
                <p className="text-xs text-gray-500">
                  Choose a prior date to inspect attendance and calculate unpaid pay.
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setAdjustmentForm(prev => ({
          ...prev,
          backpayItems: [
            ...prev.backpayItems,
            { ...EMPTY_BACKPAY_ITEM },
          ],
        }))}
        className="mt-2 flex items-center rounded px-1 text-xs font-medium text-red-600 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
      >
        <Plus className="mr-1 h-3 w-3" />
        Add Backpay
      </button>
    </div>
  );

  const renderLineItemSection = (label, icon, iconBg, iconColor, fieldKey, placeholder) => (
    <div className="mb-4">
      <label className="form-label-admin">{label}</label>
      {adjustmentForm[fieldKey].map((item, index) => (
        <div key={index} className="mb-2 flex items-center gap-2 p-0.5 -m-0.5">
          <input
            type="text"
            placeholder={placeholder || "Description"}
            value={item.description}
            onChange={(e) => {
              const updated = [...adjustmentForm[fieldKey]];
              updated[index] = { ...updated[index], description: e.target.value };
              setAdjustmentForm(prev => ({ ...prev, [fieldKey]: updated }));
            }}
            className="input-admin flex-1 text-sm"
          />
          <div className="relative w-32">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
            <input
              type="number"
              step="0.01"
              placeholder="0.00"
              onWheel={(e) => e.target.blur()}
              value={item.amount}
              onChange={(e) => {
                const updated = [...adjustmentForm[fieldKey]];
                updated[index] = { ...updated[index], amount: e.target.value };
                setAdjustmentForm(prev => ({ ...prev, [fieldKey]: updated }));
              }}
              className="input-admin w-full text-sm pl-7 text-right"
            />
          </div>
          <button
            onClick={() => {
              const updated = adjustmentForm[fieldKey].filter((_, i) => i !== index);
              setAdjustmentForm(prev => ({ ...prev, [fieldKey]: updated }));
            }}
            className="icon-button-admin icon-button-sm"
            title="Remove"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button
        onClick={() => setAdjustmentForm(prev => ({
          ...prev,
          [fieldKey]: [...prev[fieldKey], { description: '', amount: '' }]
        }))}
        className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center mt-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded px-1"
      >
        <Plus className="w-3 h-3 mr-1" />
        Add {label}
      </button>
    </div>
  );

  const sp = payrollData;
  const cb = adjustmentPreview?.component_breakdown;
  const isWorker = sp.employee_classification === 'worker';

  const hasPreviousAdjustments = (() => {
    const adj = payrollData?.adjustment_line_items || {};
    if (Object.values(adj).some(arr => Array.isArray(arr) && arr.length > 0)) return true;
    const breakdown = payrollData?.other_deductions_breakdown || {};
    const li = breakdown.line_items;
    if (li && Object.values(li).some(arr => Array.isArray(arr) && arr.length > 0)) return true;
    return Object.entries(breakdown).some(([, val]) => typeof val === 'number' && val > 0);
  })();

  const otherEarningsFallback = parseFloat(sp.commission || 0) + parseFloat(sp.public_holiday_pay || 0)
    + parseFloat(sp.rest_day_pay || 0) + parseFloat(sp.other_payments || 0);

  const earningsRows = [
    { label: 'Basic Salary', key: 'basic_salary', fallback: sp.basic_salary_earned },
    { label: 'Overtime', key: 'overtime', fallback: sp.overtime_pay },
    { label: 'Allowances', key: 'allowances', fallback: sp.total_allowances },
    { label: 'Backpay', key: 'backpay', fallback: 0 },
    { label: 'Bonus', key: 'bonus', fallback: sp.bonus_payments },
    { label: 'Other Earnings', key: 'other_earnings', fallback: otherEarningsFallback },
  ];

  const activeDeductionItems = (adjustmentForm.deductionItems || []).filter(i => i.description && parseFloat(i.amount));
  const deductionLabel = activeDeductionItems.length > 0
    ? activeDeductionItems.map(i => i.description).join(', ')
    : 'Addtl. Deductions';

  const deductionRows = [
    { label: 'Leave Deductions', key: 'leave_deductions', fallback: sp.leave_deduction_amount },
    ...(!isWorker ? [
      { label: 'CPF (Employee)', key: 'cpf_employee', fallback: sp.cpf_employee_contribution },
      { label: 'CPF (Employer)', key: 'cpf_employer', fallback: sp.cpf_employer_contribution },
    ] : []),
    { label: deductionLabel, key: 'additional_deductions', fallback: sp.other_deductions },
  ];

  const getVals = (key, fallback) => {
    const c = cb?.[key];
    return {
      before: c ? c.before : parseFloat(fallback || 0),
      after: c ? c.after : parseFloat(fallback || 0),
      change: c ? c.change : 0,
    };
  };
  const isVisible = (key, fallback) => {
    const v = getVals(key, fallback);
    return v.before > 0 || v.after > 0 || v.change !== 0;
  };

  const otDetail = cb?.overtime?.detail;

  const renderRow = (key, label, fallback, positive = 'green') => {
    const v = getVals(key, fallback);
    const upColor = positive === 'green' ? 'text-green-600' : 'text-red-600';
    const downColor = positive === 'green' ? 'text-red-600' : 'text-green-600';
    return (
      <tr key={key}>
        <td className="py-1.5 text-gray-600">
          {key === 'overtime' && otDetail && (otDetail.weekday_overtime_pay > 0 || otDetail.rest_day_overtime_pay > 0) ? (
            <div className="tooltip-container">
              <span className="tooltip-trigger cursor-help border-b border-dashed border-gray-400">{label}</span>
              <div className="tooltip-top" style={{ minWidth: '160px' }}>
                <div className="tooltip-arrow"></div>
                <div className="text-xs space-y-1">
                  {otDetail.weekday_overtime_pay > 0 && (
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-400">Weekday OT</span>
                      <span className="text-white tabular-nums">{formatCurrency(otDetail.weekday_overtime_pay)}</span>
                    </div>
                  )}
                  {otDetail.rest_day_overtime_pay > 0 && (
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-400">Rest Day OT</span>
                      <span className="text-white tabular-nums">{formatCurrency(otDetail.rest_day_overtime_pay)}</span>
                    </div>
                  )}
                  {v.change !== 0 && (
                    <div className="flex justify-between gap-3 border-t border-gray-600 pt-1 mt-1">
                      <span className="text-gray-400">Adjustment</span>
                      <span className="text-white tabular-nums">+{formatCurrency(v.change)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : label}
        </td>
        <td className="py-1.5 text-right text-gray-500 tabular-nums">{formatCurrency(v.before)}</td>
        <td className="py-1.5 text-right font-medium tabular-nums">{formatCurrency(v.after)}</td>
        <td className={`py-1.5 text-right font-medium tabular-nums ${v.change > 0 ? upColor : v.change < 0 ? downColor : 'text-gray-400'}`}>
          {v.change !== 0 ? <>{v.change > 0 ? '+' : ''}{formatCurrency(v.change)}</> : '—'}
        </td>
      </tr>
    );
  };

  const renderSubtotal = (label, key, fallback, borderClass = 'border-t border-gray-200') => {
    const before = adjustmentPreview?.before?.[key] ?? parseFloat(fallback || 0);
    const after = adjustmentPreview?.after?.[key] ?? parseFloat(fallback || 0);
    const change = adjustmentPreview?.changes?.[key] ?? 0;
    return (
      <tr key={key} className={borderClass}>
        <td className="py-1.5 font-semibold text-gray-900">{label}</td>
        <td className="py-1.5 text-right text-gray-600 tabular-nums">{formatCurrency(before)}</td>
        <td className="py-1.5 text-right font-semibold tabular-nums">{formatCurrency(after)}</td>
        <td className={`py-1.5 text-right font-semibold tabular-nums ${change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-400'}`}>
          {change !== 0 ? <>{change > 0 ? '+' : ''}{formatCurrency(change)}</> : '—'}
        </td>
      </tr>
    );
  };

  const visibleEarnings = earningsRows.filter(r => isVisible(r.key, r.fallback));
  const visibleDeductions = deductionRows.filter(r => isVisible(r.key, r.fallback));

  // Named repayment lines. These stay out of the Earnings group: a course
  // fee paid back is not an earnings adjustment and does not move gross.
  const reimbursementPreviewRows = (() => {
    const stored = ((sp.adjustment_line_items || {}).reimbursements || [])
      .filter((item) => parseFloat(item.amount) > 0);
    const draft = (adjustmentForm.reimbursementItems || [])
      .filter((item) => parseFloat(item.amount) > 0 || (item.description || '').trim());
    const used = new Set();
    const rows = draft.map((item, index) => {
      const description = (item.description || '').trim();
      const after = parseFloat(item.amount) || 0;
      const matchIndex = stored.findIndex((entry, storedIndex) => (
        !used.has(storedIndex) && (entry.description || '').trim() === description
      ));
      if (matchIndex >= 0) used.add(matchIndex);
      const before = matchIndex >= 0 ? (parseFloat(stored[matchIndex].amount) || 0) : 0;
      return {
        key: `reimburse-${index}`,
        label: description || 'Reimbursement',
        before,
        after,
        change: after - before,
      };
    });
    stored.forEach((entry, storedIndex) => {
      if (used.has(storedIndex)) return;
      const before = parseFloat(entry.amount) || 0;
      rows.push({
        key: `reimburse-removed-${storedIndex}`,
        label: (entry.description || '').trim() || 'Reimbursement',
        before,
        after: 0,
        change: -before,
      });
    });
    return rows.filter((row) => row.before > 0 || row.after > 0);
  })();
  const showReimbursementPreview = reimbursementPreviewRows.length > 0
    || parseFloat(sp.total_reimbursements || 0) > 0
    || (adjustmentPreview?.changes?.reimbursements ?? 0) !== 0;
  const isBackpayRateLoading = adjustmentForm.backpayItems.some(
    item => item.rateStatus === 'loading'
  );

  return (
    <Portal>
      <div
        className="modal-overlay"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) handleClose();
        }}
      >
        <div
          className="modal-container modal-container-xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="payroll-adjustment-title"
        >
          <div className="modal-header">
            <div>
              <h3 id="payroll-adjustment-title" className="modal-title">Adjust Payroll</h3>
              <p className="text-sm text-gray-600 mt-0.5 flex items-center gap-2">
                {payrollData.employee_first_name} {payrollData.employee_last_name}
                <InfoBadge color="gray" label={payrollData.employee_staff_id} />
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="modal-close-admin"
              aria-label="Close payroll adjustment"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="modal-content !overflow-hidden !p-0 flex flex-col">
            <div className="grid grid-cols-5 gap-4 h-full p-5">
              {/* Left Column — Form (2 cols) */}
              <div className="col-span-2 min-h-0 overflow-y-auto overscroll-y-contain -ml-1 space-y-4 py-0.5 pl-1 pr-2">
                {hasPreviousAdjustments && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-700 flex items-center">
                      <FileText className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                      Existing deductions/adjustments loaded — edit or remove as needed.
                    </p>
                  </div>
                )}

                {/* Earnings Section */}
                <div className="border-b border-gray-200 pb-5">
                  <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                      <TrendingUp className="w-3.5 h-3.5 text-green-600" />
                    </div>
                    Earnings Adjustments
                  </h5>
                  {renderBackpaySection()}
                  {renderLineItemSection('Overtime', null, null, null, 'overtimeItems')}
                  {renderLineItemSection('Allowances', null, null, null, 'allowanceItems')}
                  {renderLineItemSection('Bonus (Additional Wages)', null, null, null, 'bonusItems')}
                </div>

                {/* Deductions Section */}
                <div className="border-b border-gray-200 pb-5">
                  <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-2">
                      <TrendingUp className="w-3.5 h-3.5 text-red-600 rotate-180" />
                    </div>
                    Deduction Adjustments
                  </h5>

                  {adjustmentForm.deductionItems.map((item, index) => (
                    <div key={index} className="mb-3">
                      <div className="flex items-center gap-2 p-0.5 -m-0.5">
                        <div className="min-w-0 flex-1 overflow-visible">
                          <FormSelect
                            options={DEDUCTION_ADJUSTMENT_TYPES}
                            value={item.deductionType}
                            onChange={(val) => {
                              const updated = [...adjustmentForm.deductionItems];
                              const label = DEDUCTION_ADJUSTMENT_TYPES.find(t => t.value === val)?.label || val;
                              updated[index] = { ...updated[index], description: val === 'other' ? '' : label, deductionType: val };
                              setAdjustmentForm(prev => ({ ...prev, deductionItems: updated }));
                            }}
                            placeholder="Select type..."
                            theme="admin"
                          />
                        </div>
                        <div className="relative w-32">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            onWheel={(e) => e.target.blur()}
                            value={item.amount}
                            onChange={(e) => {
                              const updated = [...adjustmentForm.deductionItems];
                              updated[index] = { ...updated[index], amount: e.target.value };
                              setAdjustmentForm(prev => ({ ...prev, deductionItems: updated }));
                            }}
                            className="input-admin w-full text-sm pl-7 text-right"
                          />
                        </div>
                        <button
                          onClick={() => {
                            const updated = adjustmentForm.deductionItems.filter((_, i) => i !== index);
                            setAdjustmentForm(prev => ({ ...prev, deductionItems: updated }));
                          }}
                          className="icon-button-admin icon-button-sm"
                          title="Remove"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {item.deductionType === 'other' && (
                        <input
                          type="text"
                          placeholder="Specify deduction..."
                          value={item.description}
                          onChange={(e) => {
                            const updated = [...adjustmentForm.deductionItems];
                            updated[index] = { ...updated[index], description: e.target.value };
                            setAdjustmentForm(prev => ({ ...prev, deductionItems: updated }));
                          }}
                          className="input-admin w-full text-sm mt-2"
                        />
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => setAdjustmentForm(prev => ({
                      ...prev,
                      deductionItems: [...prev.deductionItems, { description: '', amount: '', deductionType: '' }]
                    }))}
                    className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center mt-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded px-1"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Deduction
                  </button>
                </div>

                {/* Reimbursements Section */}
                <div className="border-b border-gray-200 pb-5">
                  <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                      <CreditCard className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    Reimbursements
                    <span className="ml-2 text-xs text-gray-400 font-normal">(Non-taxable)</span>
                  </h5>
                  {renderLineItemSection('Reimbursement', null, null, null, 'reimbursementItems')}
                </div>

                {/* Notes */}
                <div>
                  <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                      <FileText className="w-3.5 h-3.5 text-gray-600" />
                    </div>
                    Notes
                  </h5>
                  <textarea
                    value={adjustmentForm.adjustmentNotes}
                    onChange={(e) => setAdjustmentForm(prev => ({ ...prev, adjustmentNotes: e.target.value }))}
                    placeholder={
                      adjustmentForm.backpayItems.some(item => item.isRateOverridden)
                        ? "Required: explain why the suggested backpay amount was changed..."
                        : "Optional context for this adjustment..."
                    }
                    className="textarea-admin w-full text-sm"
                    rows={2}
                  />
                </div>
              </div>

              {/* Right Column — Impact Preview (3 cols) */}
              <div className="col-span-3">
                <div className="sticky top-0 bg-gray-50 rounded-xl p-4">
                  <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-2">
                      <Calculator className="w-3.5 h-3.5 text-purple-600" />
                    </div>
                    Impact Preview
                    {isPreviewLoading ? (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        Calculating...
                      </span>
                    ) : adjustmentPreview ? (
                      <>
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Recalculated
                        </span>
                        {adjustmentForm.backpayItems.filter(item => (
                          item.targetDate && parseFloat(item.amount)
                        )).length > 0
                          && adjustmentForm.backpayItems
                            .filter(item => item.targetDate && parseFloat(item.amount))
                            .every(item => (
                              item.rateStatus === 'success'
                              && !item.isRateOverridden
                              && item.calculationFingerprint
                            )) && (
                          <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Verified source
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        No changes
                      </span>
                    )}
                  </h5>

                  <div className="overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="table-header">
                        <tr className="border-b border-gray-100">
                          <th className="table-header-cell"></th>
                          <th className="table-header-cell text-right">Before</th>
                          <th className="table-header-cell text-right">After</th>
                          <th className="table-header-cell text-right">Change</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleEarnings.length > 0 && (
                          <tr><td colSpan={4} className="pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Earnings</td></tr>
                        )}
                        {visibleEarnings.map(r => renderRow(r.key, r.label, r.fallback, 'green'))}
                        {renderSubtotal('Gross Pay', 'gross_pay', sp.gross_pay)}

                        {visibleDeductions.length > 0 && (
                          <tr><td colSpan={4} className="pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Deductions</td></tr>
                        )}
                        {visibleDeductions.map(r => renderRow(r.key, r.label, r.fallback, 'red'))}
                        {renderSubtotal('Net Pay', 'net_pay', sp.net_pay)}

                        {showReimbursementPreview && (
                          <>
                            <tr><td colSpan={4} className="pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Reimbursements</td></tr>
                            {reimbursementPreviewRows.length > 0 ? reimbursementPreviewRows.map((row) => (
                              <tr key={row.key}>
                                <td className="py-1.5 text-gray-600">{row.label}</td>
                                <td className="py-1.5 text-right text-gray-500 tabular-nums">{formatCurrency(row.before)}</td>
                                <td className="py-1.5 text-right font-medium tabular-nums">{formatCurrency(row.after)}</td>
                                <td className={`py-1.5 text-right font-medium tabular-nums ${row.change > 0 ? 'text-green-600' : row.change < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                  {row.change !== 0 ? <>{row.change > 0 ? '+' : ''}{formatCurrency(row.change)}</> : '—'}
                                </td>
                              </tr>
                            )) : (
                              <tr>
                                <td className="py-1.5 text-gray-600">Reimbursements</td>
                                <td className="py-1.5 text-right text-gray-500 tabular-nums">{formatCurrency(adjustmentPreview?.before?.reimbursements ?? parseFloat(sp.total_reimbursements || 0))}</td>
                                <td className="py-1.5 text-right font-medium tabular-nums">{formatCurrency(adjustmentPreview?.after?.reimbursements ?? parseFloat(sp.total_reimbursements || 0))}</td>
                                <td className={`py-1.5 text-right font-medium tabular-nums ${(adjustmentPreview?.changes?.reimbursements ?? 0) > 0 ? 'text-green-600' : (adjustmentPreview?.changes?.reimbursements ?? 0) < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                  {(adjustmentPreview?.changes?.reimbursements ?? 0) !== 0 ? <>{(adjustmentPreview?.changes?.reimbursements ?? 0) > 0 ? '+' : ''}{formatCurrency(adjustmentPreview?.changes?.reimbursements ?? 0)}</> : '—'}
                                </td>
                              </tr>
                            )}
                          </>
                        )}

                        <tr className="border-t-2 border-gray-200">
                          <td className="py-2 font-bold text-gray-900">Final Payment</td>
                          <td className="py-2 text-right text-gray-600 tabular-nums">{formatCurrency(adjustmentPreview?.before?.final_payment ?? sp.final_payment)}</td>
                          <td className="py-2 text-right font-bold text-gray-900 tabular-nums">{formatCurrency(adjustmentPreview?.after?.final_payment ?? sp.final_payment)}</td>
                          <td className={`py-2 text-right font-bold tabular-nums ${(adjustmentPreview?.changes?.final_payment ?? 0) > 0 ? 'text-green-600' : (adjustmentPreview?.changes?.final_payment ?? 0) < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                            {(adjustmentPreview?.changes?.final_payment ?? 0) !== 0 ? (
                              <>{(adjustmentPreview?.changes?.final_payment ?? 0) > 0 ? '+' : ''}{formatCurrency(adjustmentPreview?.changes?.final_payment ?? 0)}</>
                            ) : '—'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {adjustmentPreview?.warnings?.length > 0 && (
                    <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-700">
                          {adjustmentPreview.warnings.map((w, i) => (
                            <p key={i}>{w}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {adjustmentPreview?.will_recalculate_cpf && !isWorker && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-700 flex items-center">
                        <Shield className="w-3.5 h-3.5 mr-1.5" />
                        CPF contributions will be recalculated automatically
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={handleClose} className="btn btn-admin-outline">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={
                adjustPayrollMutation.isPending
                || isBackpayRateLoading
                || adjustmentForm.backpayItems.some(
                  item => item.rateStatus === 'manual_review' && !parseFloat(item.amount)
                )
              }
              className="btn btn-admin-primary"
            >
              {adjustPayrollMutation.isPending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Apply Adjustments
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export { EMPTY_BACKPAY_ITEM, applyBackpayPreview, mapStoredBackpayItem };
export default PayrollAdjustmentModal;
